import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from 'react-bootstrap/Button';
import { LuChevronsDown } from 'react-icons/lu';

import Slider from 'react-slick';
import { Tooltip } from 'react-tooltip';
import { AiOutlineFilter } from 'react-icons/ai';
import { BiSortAlt2 } from 'react-icons/bi';

import { useProject } from '@contexts/Project.context';

import SliderImage from './SliderImage';
import IndexSlider from './IndexSlider';
import SliderToolC from './SliderTool';
import SortSingleImage from './SortSingleImage';
import FilterSingleImage from './FilterSingleImage';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const Container = styled.div`
	--bg-color: ${({ theme }) => theme.colors.background};

	position: absolute;
	z-index: 1;
	bottom: 36px;
	left: 0;

	display: grid;

	background: ${({ $open, theme }) => ($open ? theme.colors.background : 'transparent')};

	width: 100%;
	padding-top: 5px;

	transition: 0.2s ease-in-out;
	translate: 0 ${({ $open }) => ($open ? '0' : 'calc(100% - 40px)')};
	pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
`;

const CarouselContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	width: 100%;
	min-height: 85px;
	overflow: hidden;

	opacity: ${({ $open }) => ($open ? 1 : 0)};
`;

const CarouselItems = styled.div`
	width: 100%;
	margin-top: 0px;
	margin-bottom: 10px;
`;

const CarouselWrapper = styled.div`
	width: 100%;
	max-height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding-left: 30px;
	padding-right: 30px;
	padding-bottom: 10px;
	background-color: transparent;
	margin-bottom: 10px;
`;

const ToolsContainer = styled.div`
	display: grid;
	grid-template-columns: 25px 25px 1fr;
	grid-template-rows: 27.5px;
	gap: 12px;

	margin-inline: 38px;
`;

const SliderTool = styled(SliderToolC)`
	opacity: ${({ $open }) => ($open ? '1' : '0')};
`;

const CollapseButton = styled(Button)`
	grid-column: 3;

	display: grid;
	place-items: center;
	align-self: start;

	min-width: 96px;
	margin-inline: auto;
	padding-block: 2px;
	padding-inline: 4px;

	pointer-events: auto !important;
`;

const ArrowIcon = styled(LuChevronsDown)`
	rotate: ${({ $open }) => ($open ? 0 : 180)}deg;
`;

export const StyledTooltip = styled(Tooltip)`
	z-index: 50;
`;

const IndexSliderContainer = styled.div`
	display: grid;
	grid-template-columns: 50px auto 50px;
	align-items: center;
	color: ${props => props.theme.colors.textAccent};
	font-size: 0.8rem;
`;
const Number = styled.div`
	text-align: center;
`;

const CarouselSlider = () => {
	const {
		singleImageFeatures,
		filteredImages,
		activeFilename,
		interactionsDisabled,
		singleImageSliderOpen: open,
		dispatch,
	} = useProject();

	const toggleOpen = () =>
		dispatch({
			type: 'setSingleImageSliderOpen',
			payload: !open,
		});

	let sliderRef = useRef(null);
	const [slideIndex, setSlideIndex] = useState([0]);
	const [slidesToShow, setSlidesToShow] = useState(0);

	const [tools, setTools] = useState([
		{ name: 'filtering', isActive: false },
		{ name: 'sorting', isActive: false },
	]);

	const hasDetections = singleImageFeatures?.some(
		item => item.get('detections')?.classes.length > 0
	);

	// Slides to show can not be more items.length because of bug in slick slider
	const slidesToShowLarge = Math.min(20, filteredImages?.length);
	const slidesToShowMedium = Math.min(10, filteredImages?.length);
	const slidesToShowSmall = Math.min(5, filteredImages?.length);

	const settings = {
		lazyLoad: 'ondemand',
		infinite: false,
		slidesToShow: slidesToShowLarge,
		slidesToScroll: slidesToShowLarge,
		centerPadding: '60px',
		initialSlide: 0,
		speed: 500,
		arrows: !interactionsDisabled,
		responsive: [
			{
				breakpoint: 2000,
				settings: {
					slidesToShow: slidesToShowMedium,
					slidesToScroll: slidesToShowMedium,
					beforeChange: (current, next) => {
						setSlideIndex([next]);
						setSlidesToShow(slidesToShowMedium);
					},
				},
			},
			{
				breakpoint: 1000,
				settings: {
					slidesToShow: slidesToShowSmall,
					slidesToScroll: slidesToShowSmall,
					beforeChange: (current, next) => {
						setSlideIndex([next]);
						setSlidesToShow(slidesToShowSmall);
					},
				},
			},
		],
		beforeChange: (current, next) => {
			setSlideIndex([next]);
			setSlidesToShow(slidesToShowLarge);
		},
	};

	useEffect(() => {
		if (activeFilename)
			sliderRef.current.slickGoTo(
				filteredImages.findIndex(item => item.get('name') == activeFilename)
			);
	}, [activeFilename]);

	if (!singleImageFeatures?.length) {
		return null;
	}

	return (
		<Container $open={open}>
			<ToolsContainer>
				<StyledTooltip id="slider-tool-tooltip" variant="dark" />
				{hasDetections && (
					<>
						<SliderTool
							tools={tools}
							setTools={setTools}
							toolId="sorting"
							toolName="Sort Images"
							tooltipId="slider-tool-tooltip"
							icon={<BiSortAlt2 />}
							$open={open}
							inert={!open}>
							<SortSingleImage tools={tools} setTools={setTools} />
						</SliderTool>
						<SliderTool
							tools={tools}
							setTools={setTools}
							toolId="filtering"
							toolName="Filter Images"
							tooltipId="slider-tool-tooltip"
							icon={<AiOutlineFilter />}
							$open={open}
							inert={!open}>
							<FilterSingleImage tools={tools} setTools={setTools} />
						</SliderTool>
					</>
				)}
				<CollapseButton
					data-tooltip-id="slider-tool-tooltip"
					data-tooltip-content={open ? 'Hide Image Slider' : 'Show Image Slider'}
					variant="dark"
					onClick={toggleOpen}
					aria-expanded={open}>
					<ArrowIcon $open={open} />
				</CollapseButton>
			</ToolsContainer>

			<CarouselContainer $open={open} inert={!open}>
				<CarouselWrapper>
					<CarouselItems>
						{filteredImages?.length > 0 && (
							<Slider
								ref={slider => {
									sliderRef.current = slider;
								}}
								{...settings}>
								{filteredImages?.map((item, index) => (
									<SliderImage
										key={item.get('name')}
										item={item}
										sliderRef={sliderRef}
										index={index + 1}
										disabled={interactionsDisabled}
										active={activeFilename == item.get('name')}
									/>
								))}
							</Slider>
						)}
					</CarouselItems>

					{filteredImages?.length > 10 && (
						<IndexSliderContainer>
							<Number>1</Number>
							<IndexSlider
								max={filteredImages.length - slidesToShow}
								sliderRef={sliderRef}
								slideIndex={slideIndex}
								setSlideIndex={setSlideIndex}
								disabled={interactionsDisabled}
							/>
							<Number>{filteredImages.length}</Number>
						</IndexSliderContainer>
					)}
				</CarouselWrapper>
			</CarouselContainer>
		</Container>
	);
};

export default CarouselSlider;
